import {ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, OnInit, TemplateRef, ViewChild} from '@angular/core';
import {CommonModule, DatePipe} from '@angular/common';
import {FormBuilder, FormGroup, ReactiveFormsModule, Validators} from '@angular/forms';
import {MatButtonModule} from '@angular/material/button';
import {MatFormFieldModule} from '@angular/material/form-field';
import {MatInputModule} from '@angular/material/input';
import {MatIconModule} from '@angular/material/icon';
import {MatProgressBarModule} from '@angular/material/progress-bar';
import {MatCardModule} from '@angular/material/card';
import {MatSnackBar, MatSnackBarModule} from '@angular/material/snack-bar';
import {MatDividerModule} from '@angular/material/divider';
import {MatTooltipModule} from '@angular/material/tooltip';
import {MatDialog, MatDialogModule} from '@angular/material/dialog';
import {MatTabsModule} from '@angular/material/tabs';
import {RestFactoryService} from '../../../core/services/rest-factory.service';
import {finalize, take} from 'rxjs';
import {NgxQrcodeStylingComponent, Options} from 'ngx-qrcode-styling';

interface QrResponse {
    ds_Web_url: string;
    ds_App_url: string;
    dt_expiration: any;
    vi_uses_left: number;
}

@Component({
    selector: 'app-qr-free-subscription',
    standalone: true,
    imports: [
        CommonModule,
        ReactiveFormsModule,
        MatButtonModule,
        MatFormFieldModule,
        MatInputModule,
        MatIconModule,
        MatProgressBarModule,
        MatCardModule,
        MatSnackBarModule,
        MatDividerModule,
        MatTooltipModule,
        MatDialogModule,
        MatTabsModule,
        NgxQrcodeStylingComponent,
        DatePipe
    ],
    templateUrl: './qr-free-subscription.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class QrFreeSubscriptionComponent implements OnInit {
    @ViewChild('mobileQrCode', {static: false}) mobileQrCode!: NgxQrcodeStylingComponent;
    @ViewChild('desktopQrCode', {static: false}) desktopQrCode!: NgxQrcodeStylingComponent;
    @ViewChild('bigMobileQrCode', {static: false}) bigMobileQrCode!: NgxQrcodeStylingComponent;
    @ViewChild('bigDesktopQrCode', {static: false}) bigDesktopQrCode!: NgxQrcodeStylingComponent;

    @ViewChild('bigMobileQrCode', {static: false, read: ElementRef}) bigMobileEl!: ElementRef;
    @ViewChild('bigDesktopQrCode', {static: false, read: ElementRef}) bigDesktopEl!: ElementRef;

    form: FormGroup;
    isGenerating = false;
    qrResponse: QrResponse | null = null;
    canShareFiles = false;

    private readonly themeColor = 'rgb(184 156 76)';

    baseQrConfig: Options = {
        width: 300,
        height: 300,
        type: 'canvas',
        margin: 10,
        data: '',
        image: '',
        qrOptions: {typeNumber: 0, mode: 'Byte', errorCorrectionLevel: 'H'},
        imageOptions: {hideBackgroundDots: true, imageSize: 0.45, margin: 5, crossOrigin: 'anonymous'},
        dotsOptions: {color: '#000000', type: 'square'},
        backgroundOptions: {color: '#ffffff'},
        cornersSquareOptions: {color: '#000000', type: 'square'},
        cornersDotOptions: {color: '#000000', type: 'square'}
    };

    mobileQrConfig: Options = {...this.baseQrConfig};
    desktopQrConfig: Options = {...this.baseQrConfig};
    bigMobileQrConfig: Options = {...this.baseQrConfig, width: 1024, height: 1024};
    bigDesktopQrConfig: Options = {...this.baseQrConfig, width: 1024, height: 1024};

    private readonly API_CREATE_QR = '/admin/autosubscription/generategift';

    constructor(
        private _fb: FormBuilder,
        private _restFactoryService: RestFactoryService,
        private _cd: ChangeDetectorRef,
        private _snackBar: MatSnackBar,
        public _dialog: MatDialog
    ) {
    }

    ngOnInit(): void {
        this.form = this._fb.group({
            vi_subscriptions: [1, [Validators.required, Validators.min(1), Validators.max(10000)]],
            ds_description: ['', [Validators.required, Validators.minLength(3), Validators.maxLength(255)]],
            ds_centerText: ['Free Subscription', [Validators.required, Validators.maxLength(60)]]
        });

        try {
            const probe = new File([new Blob()], 'probe.png', {type: 'image/png'});
            this.canShareFiles = !!(navigator.canShare && navigator.canShare({files: [probe]}));
        } catch {
            this.canShareFiles = false;
        }
    }

    generateQr(): void {
        if (this.form.invalid) {
            this.form.markAllAsTouched();
            return;
        }

        this.isGenerating = true;
        this.qrResponse = null;

        const payload = new FormData();
        payload.append('vi_uses', this.form.value.vi_subscriptions);
        payload.append('ds_comment', this.form.value.ds_description);

        this._restFactoryService.httpPostService(this.API_CREATE_QR, payload)
            .pipe(
                take(1),
                finalize(() => {
                    this.isGenerating = false;
                    this._cd.markForCheck();
                })
            )
            .subscribe({
                next: (response: QrResponse) => {
                    if (response && response.ds_App_url && response.ds_Web_url) {
                        this.qrResponse = response;
                        this.buildQrConfigs();
                        this._snackBar.open('QR Codes generated!', 'Dismiss', {duration: 3000});
                        this._cd.markForCheck();
                    } else {
                        this._snackBar.open('Invalid response from server.', 'Close', {duration: 3000});
                    }
                },
                error: () => {
                    this._snackBar.open('Error generating QR codes.', 'Close', {duration: 3000});
                }
            });
    }

    private buildQrConfigs(): void {
        if (!this.qrResponse) return;

        const centerText = this.form.value.ds_centerText || 'Free Subscription';
        const labelImage = this.createLabelImage(centerText, this.themeColor);

        this.mobileQrConfig = {...this.baseQrConfig, data: this.qrResponse.ds_App_url, image: labelImage};
        this.desktopQrConfig = {...this.baseQrConfig, data: this.qrResponse.ds_Web_url, image: labelImage};
        this.bigMobileQrConfig = {
            ...this.baseQrConfig,
            width: 1024,
            height: 1024,
            data: this.qrResponse.ds_App_url,
            image: labelImage
        };
        this.bigDesktopQrConfig = {
            ...this.baseQrConfig,
            width: 1024,
            height: 1024,
            data: this.qrResponse.ds_Web_url,
            image: labelImage
        };
    }

    refreshCenterText(): void {
        if (this.qrResponse) {
            this.buildQrConfigs();
        }
    }

    resetForm(): void {
        this.form.reset({vi_subscriptions: 1, ds_description: '', ds_centerText: 'Free Subscription'});
        this.qrResponse = null;
        this._cd.markForCheck();
    }

    downloadQr(type: 'mobile' | 'desktop'): void {
        const name = `free-subscription-${type}.png`;
        if (type === 'mobile' && this.bigMobileQrCode) {
            this.bigMobileQrCode.download(name);
        } else if (type === 'desktop' && this.bigDesktopQrCode) {
            this.bigDesktopQrCode.download(name);
        }
    }

    async shareQr(type: 'mobile' | 'desktop'): Promise<void> {
        const elRef = type === 'mobile' ? this.bigMobileEl : this.bigDesktopEl;
        const canvas: HTMLCanvasElement | null = elRef?.nativeElement?.querySelector('canvas');

        if (!canvas) {
            this._snackBar.open('QR is not ready yet.', 'Close', {duration: 2000});
            return;
        }

        canvas.toBlob(async (blob) => {
            if (!blob) {
                this.downloadQr(type);
                return;
            }
            const file = new File([blob], `free-subscription-${type}.png`, {type: 'image/png'});

            if (navigator.canShare && navigator.canShare({files: [file]})) {
                try {
                    await navigator.share({
                        files: [file],
                        title: 'Free Subscription QR',
                        text: 'Scan this QR to claim your free subscription.'
                    });
                } catch (e: any) {
                    if (e?.name !== 'AbortError') {
                        this._snackBar.open('Share canceled or failed.', 'Close', {duration: 2000});
                    }
                }
            } else {
                this.downloadQr(type);
            }
        }, 'image/png');
    }

    openZoomedQr(templateRef: TemplateRef<any>): void {
        this._dialog.open(templateRef, {
            maxWidth: '98vw',
            maxHeight: '98vh',
            width: 'auto',
            height: 'auto',
            panelClass: 'bg-transparent',
            backdropClass: 'backdrop-blur-sm',
            autoFocus: false
        });
    }

    copyUrl(url: string | undefined): void {
        if (!url) return;
        navigator.clipboard.writeText(url).then(() => {
            this._snackBar.open('URL copied!', 'Close', {duration: 2000});
        });
    }

    private createLabelImage(text: string, color: string): string {
        const size = 1024;
        const padding = 20;
        const borderThickness = 40;
        const center = size / 2;
        const radius = (size / 2) - padding;

        const canvas = document.createElement('canvas');
        canvas.width = size;
        canvas.height = size;

        const ctx = canvas.getContext('2d');
        if (!ctx) return '';

        ctx.fillStyle = '#ffffff';
        ctx.beginPath();
        ctx.arc(center, center, radius, 0, 2 * Math.PI);
        ctx.fill();

        ctx.lineWidth = borderThickness;
        ctx.strokeStyle = color;
        ctx.stroke();

        const safeWidth = radius * 1.5;
        const safeHeight = radius * 1.5;

        ctx.fillStyle = '#222222';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';

        let fontSize = 200;
        let lines: string[] = [];
        let lineHeight = 0;
        const minFontSize = 40;

        while (fontSize >= minFontSize) {
            ctx.font = `bold ${fontSize}px "Roboto", "Helvetica Neue", "Arial", sans-serif`;
            lineHeight = fontSize * 1.15;
            lines = this.getWrappedLines(ctx, text, safeWidth);
            const totalHeight = lines.length * lineHeight;
            if (totalHeight <= safeHeight) break;
            fontSize -= 10;
        }

        const totalBlockHeight = lines.length * lineHeight;
        const startY = center - (totalBlockHeight / 2) + (lineHeight / 2) - (fontSize * 0.1);

        lines.forEach((line, index) => {
            ctx.fillText(line, center, startY + (index * lineHeight));
        });

        return canvas.toDataURL();
    }

    private getWrappedLines(ctx: CanvasRenderingContext2D, text: string, maxWidth: number): string[] {
        const words = text.split(' ');
        const lines: string[] = [];
        let currentLine = words[0];

        const processToken = (token: string): string[] => {
            if (ctx.measureText(token).width <= maxWidth) return [token];
            const splitByChar = token.split(/([\/,-])/);
            let subLines: string[] = [];
            let subLine = "";

            for (let part of splitByChar) {
                if (ctx.measureText(subLine + part).width <= maxWidth) {
                    subLine += part;
                } else {
                    if (subLine) subLines.push(subLine);
                    if (ctx.measureText(part).width > maxWidth) {
                        let charLine = "";
                        for (let char of part) {
                            if (ctx.measureText(charLine + char).width <= maxWidth) {
                                charLine += char;
                            } else {
                                subLines.push(charLine);
                                charLine = char;
                            }
                        }
                        subLine = charLine;
                    } else {
                        subLine = part;
                    }
                }
            }
            if (subLine) subLines.push(subLine);
            return subLines;
        };

        if (ctx.measureText(currentLine).width > maxWidth) {
            const broken = processToken(currentLine);
            lines.push(...broken.slice(0, -1));
            currentLine = broken[broken.length - 1];
        }

        for (let i = 1; i < words.length; i++) {
            const word = words[i];
            const testLine = currentLine + " " + word;

            if (ctx.measureText(testLine).width <= maxWidth) {
                currentLine = testLine;
            } else {
                lines.push(currentLine);
                if (ctx.measureText(word).width > maxWidth) {
                    const broken = processToken(word);
                    lines.push(...broken.slice(0, -1));
                    currentLine = broken[broken.length - 1];
                } else {
                    currentLine = word;
                }
            }
        }
        lines.push(currentLine);
        return lines;
    }
}
